import { useState } from 'react';
import { useLeaveRequests } from '@/api/queries/leave.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { cn } from '@/lib/cn';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isSameMonth, parseISO, isWithinInterval } from 'date-fns';

const LEAVE_COLORS = ['bg-blue-400','bg-purple-400','bg-green-400','bg-yellow-400','bg-pink-400','bg-orange-400'];

export default function LeaveCalendarPage() {
  const now = new Date();
  const [month, setMonth] = useState(format(now, 'yyyy-MM'));
  const { data: resp } = useLeaveRequests({ status: 'APPROVED' });
  const requests = resp?.data || [];

  const year = parseInt(month.split('-')[0]);
  const mon = parseInt(month.split('-')[1]) - 1;
  const monthStart = startOfMonth(new Date(year, mon, 1));
  const monthEnd = endOfMonth(monthStart);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPad = getDay(monthStart);

  const employeeColors = {};
  let colorIdx = 0;
  requests.forEach(r => {
    if (!employeeColors[r.employee?.id]) {
      employeeColors[r.employee?.id] = LEAVE_COLORS[colorIdx % LEAVE_COLORS.length];
      colorIdx++;
    }
  });

  const getLeavesOnDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return requests.filter(r => {
      try {
        return isWithinInterval(parseISO(dateStr), { start: parseISO(r.from_date), end: parseISO(r.to_date) });
      } catch { return false; }
    });
  };

  return (
    <div className="space-y-5 fade-in">
      <PageHeader
        title="Leave Calendar"
        subtitle="Approved leaves by month"
        breadcrumb={[{ label: 'Leave', href: '/leave' }, { label: 'Calendar' }]}
      />

      <div className="card flex items-center justify-between p-3 max-w-xs">
        <button onClick={() => { const d = new Date(year, mon - 1, 1); setMonth(format(d, 'yyyy-MM')); }}
          className="p-2 rounded-[8px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]">←</button>
        <span className="text-[14px] font-bold text-[var(--text-primary)]">{format(new Date(year, mon, 1), 'MMMM yyyy')}</span>
        <button onClick={() => { const d = new Date(year, mon + 1, 1); setMonth(format(d, 'yyyy-MM')); }}
          className="p-2 rounded-[8px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]">→</button>
      </div>

      <div className="card p-5">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
            <div key={d} className="text-[11px] font-bold text-center text-[var(--text-tertiary)] py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: startPad }).map((_, i) => <div key={`pad-${i}`} className="min-h-[70px]"/>)}
          {days.map(day => {
            const leaves = getLeavesOnDay(day);
            const isWeekend = [0, 6].includes(getDay(day));
            const isToday = format(day, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');
            return (
              <div key={day.toISOString()} className={cn(
                'min-h-[70px] p-1 rounded-[8px] border border-transparent',
                isWeekend ? 'bg-[var(--bg-muted)]/40' : 'bg-[var(--bg-card)]',
                isToday ? 'border-[var(--accent)] ring-1 ring-[var(--accent)]/30' : ''
              )}>
                <p className={cn('text-[11px] font-bold mb-1', isToday ? 'text-[var(--accent)]' : isWeekend ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-secondary)]')}>
                  {format(day, 'd')}
                </p>
                <div className="space-y-0.5">
                  {leaves.slice(0, 2).map(r => (
                    <div key={r.id} title={`${r.employee?.name} — ${r.leave_type}`}
                      className={cn('text-[9px] font-semibold text-white px-1 rounded truncate', employeeColors[r.employee?.id] || 'bg-blue-400')}>
                      {r.employee?.name?.split(' ')[0]}
                    </div>
                  ))}
                  {leaves.length > 2 && <div className="text-[9px] text-[var(--text-tertiary)] pl-1">+{leaves.length - 2}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      {requests.length > 0 && (
        <div className="card p-5">
          <p className="text-[12px] font-semibold text-[var(--text-secondary)] mb-3">Employees on leave this month</p>
          <div className="flex flex-wrap gap-3">
            {[...new Map(requests.map(r => [r.employee?.id, r])).values()].map(r => (
              <div key={r.employee?.id} className="flex items-center gap-2">
                <div className={cn('w-3 h-3 rounded-full', employeeColors[r.employee?.id])}/>
                <span className="text-[12px] text-[var(--text-secondary)]">{r.employee?.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
